import React from 'react';
import { cn } from '@/lib/utils';
import { Volume2, Check, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function ChatMessage({ message, isTyping = false, onSpeak = null }) {
  const isUser = message.role === 'user';
  const time = message.timestamp ? new Date(message.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div 
      className={cn(
        "flex mb-1",
        isUser ? "justify-end" : "justify-start"
      )}
      data-testid={`chat-message-${message.id || 'typing'}`}
    >
      {/* Message Bubble - WhatsApp Style */}
      <div 
        className={cn(
          "relative max-w-[85%] sm:max-w-[70%] rounded-lg px-3 py-2 shadow-sm",
          isUser 
            ? "bg-[#D9FDD3] text-gray-800 rounded-tr-none" 
            : "bg-white text-gray-800 rounded-tl-none"
        )}
      >
        {/* WhatsApp-style tail */}
        <div 
          className={cn(
            "absolute top-0 w-3 h-3",
            isUser 
              ? "right-0 -mr-2 border-t-[12px] border-t-[#D9FDD3] border-r-[12px] border-r-transparent"
              : "left-0 -ml-2 border-t-[12px] border-t-white border-l-[12px] border-l-transparent"
          )}
        />
        
        {isTyping ? (
          <div className="flex gap-1 py-2 px-1">
            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
          </div>
        ) : (
          <>
            <div className="text-sm whitespace-pre-wrap leading-relaxed pr-12">
              {/* Render markdown-like formatting */}
              {message.content.split('\n').map((line, i) => {
                // Bold text
                const boldRegex = /\*\*(.+?)\*\*/g;
                const parts = line.split(boldRegex);
                
                // Handle section headers with emojis
                const isHeader = line.startsWith('##') || line.startsWith('🎯') || line.startsWith('📊') || line.startsWith('🍔') || line.startsWith('📋') || line.startsWith('🤝') || line.startsWith('⚡') || line.startsWith('---');
                
                if (line === '---') {
                  return <hr key={i} className="my-2 border-gray-200" />;
                }
                
                return (
                  <p key={i} className={cn(
                    i > 0 ? "mt-1.5" : "",
                    isHeader ? "font-semibold text-[#5BA946] mt-3 first:mt-0" : ""
                  )}>
                    {parts.map((part, j) => {
                      // Every odd index is the bold content
                      if (j % 2 === 1) {
                        return <strong key={j} className="font-semibold">{part}</strong>;
                      }
                      return <span key={j}>{part}</span>;
                    })}
                  </p>
                );
              })}
            </div>
            
            {/* Time and checkmarks - WhatsApp style */}
            <div className={cn(
              "absolute bottom-1 right-2 flex items-center gap-1 text-[10px]",
              isUser ? "text-gray-500" : "text-gray-400"
            )}>
              <span>{time}</span>
              {isUser && (
                <CheckCheck className="w-4 h-4 text-[#53BDEB]" />
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Speak button for assistant messages - outside bubble */}
      {!isUser && !isTyping && onSpeak && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onSpeak}
          className="self-end ml-1 h-6 w-6 p-0 text-gray-400 hover:text-[#5BA946] hover:bg-transparent"
          data-testid="speak-message-btn"
        >
          <Volume2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}
